import type { RegisteredEditorModule } from '../../types';
import {
  createWordsearchGame,
  detailOf,
  duplicateJogo,
  getWordsearchSource,
  isOrphanWordsearchError,
  previewWordsearch,
  replaceWordsearch,
  updateJogo,
  TIPO_JOGO,
} from '../../../../../services/admin';
import { createEmpty, type WordsearchDoc } from './doc';
import { wordsearchReducer, type WordsearchAction } from './reducer';
import { validate } from './validate';
import { coalesceKeyFor, initialWordsearchUi } from './ui';
import { blobToDoc, docToBlob } from './wire';
import { Canvas } from './Canvas';
import { Inspector } from './Inspector';

// Monta o EditorModule do caça-palavras: núcleo puro + serviços (via wire) + UI.
// O shell só conhece esta abstração — ele não aprende nada sobre grade nem blob.

const payloadFrom = (doc: WordsearchDoc) => {
  const { game, texto } = docToBlob(doc);
  return { titulo: doc.titulo, game, texto };
};

// Caça-palavras não tem numeração derivada: `indices` é sempre vazio (não há
// `deriveIndices` — de propósito, ver abaixo).
const NO_INDICES: Record<string, number> = {};

/**
 * Embrulha o erro num `Error` com contexto SEM apagar o `response` original: o shell
 * lê `e.response.status` para separar o 409 (jogo com ranking → somente leitura +
 * duplicar) de uma falha comum. Um `new Error(msg)` puro destrói essa informação e
 * qualquer falha vira um "Falha ao salvar." sem saída.
 * (TS 4.5.5 não tem o `Error(message, { cause })` do ES2022, daí a atribuição.)
 */
function wrapError(err: unknown, message: string): Error {
  const wrapped = new Error(message) as Error & { response?: unknown };
  const response = (err as { response?: unknown } | null)?.response;
  if (response !== undefined) wrapped.response = response;
  return wrapped;
}

export const wordsearchModule: RegisteredEditorModule<WordsearchDoc, WordsearchAction> = {
  kind: 'wordsearch',
  label: 'Caça-palavras',

  // Caça-palavras é JOGO (não passatempo): quem mostra o "Editar grade" dele é o
  // catálogo de Jogos, casando pelo `idTipo`.
  entry: { catalog: 'jogo', idTipo: TIPO_JOGO.CACA_PALAVRAS },

  newPuzzle: {
    title: 'Novo caça-palavras',
    description: 'Um caça-palavras novo — as palavras são colocadas arrastando na grade.',
    titlePlaceholder: 'Ex.: Bichos da fazenda',
    // Sem `tipo`: o `id_tipo` do jogo é fixado pelo endpoint de criação
    // (`createWordsearchGame`), então não há o que o autor escolher aqui.
    dimensions: { mode: 'author', largura: 12, altura: 12, min: 2, max: 30 },
  },

  createEmpty,
  initialUi: initialWordsearchUi,
  reduce: wordsearchReducer,
  validate,
  coalesceKey: coalesceKeyFor,

  load: (id) =>
    getWordsearchSource(id).then((src) => ({
      // O show_wordsearch não informa título nem ranking. Título vazio aqui: o
      // autor completa no Inspector. Ranking não informado → `editavel: true`;
      // quem barra de verdade uma edição indevida é o 409 do servidor no save.
      doc: blobToDoc({ game: src.game, texto: src.texto }, ''),
      editavel: true,
    })),

  save: (id, doc) => {
    const payload = payloadFrom(doc);
    if (id != null) {
      // O PUT de /admin/jogos/:id/wordsearch só aceita game/texto — o endpoint não tem
      // campo `titulo`. Por isso o título, quando preenchido, vai numa segunda chamada
      // ao endpoint genérico de jogo (PATCH /admin/jogos/:id). Guard vazio: logo após o
      // load o campo vem '' (show_wordsearch não devolve título) — salvar nesse estado
      // não pode apagar o título real que já está gravado no jogo.
      return replaceWordsearch(id, payload).then(() => {
        if (doc.titulo.trim() === '') return { id, indices: NO_INDICES };
        return updateJogo(id, { titulo: doc.titulo.trim() }).then(
          () => ({ id, indices: NO_INDICES }),
          (err: unknown) => {
            // A grade (replaceWordsearch acima) já foi gravada com sucesso — só o
            // título falhou. Deixamos o erro subir, em vez de engolir e seguir, porque
            // o autor pediu explicitamente para salvar um título e isso não aconteceu;
            // mas a mensagem deixa registrado que a grade está segura, para quem for
            // depurar não confundir com uma falha do save inteiro. `detailOf`, não
            // `err.message` cru: preserva o `response.data.msg` do servidor; `wrapError`
            // preserva o `response` original (o shell lê o status para o 409).
            const detail = detailOf(err);
            throw wrapError(
              err,
              `A grade foi salva, mas o título não pôde ser atualizado: ${detail}. ` +
                'Tente salvar novamente para reenviar o título.',
            );
          },
        );
      });
    }
    // createWordsearchGame cria em dois passos (create_jogo e depois
    // create_wordsearch). Quando o segundo falha, a mensagem que vem de lá
    // (`isOrphanWordsearchError`) já diz qual jogo ficou órfão e que ele precisa ser
    // excluído (ver services/admin.ts) — é auto-suficiente, e sobe sem reembrulho:
    // um conselho genérico por cima ("confira com o time") CONTRADIZ o específico
    // ("exclua o jogo #N") e só estufa a mensagem. Só o primeiro passo falhando (erro
    // do axios, cru, sem id nenhum criado ainda) ganha o contexto extra abaixo.
    return createWordsearchGame(payload).then(
      (r) => ({ id: r.id, indices: NO_INDICES }),
      (err: unknown) => {
        if (isOrphanWordsearchError(err)) throw err;
        throw wrapError(err, `Falha ao criar o caça-palavras: ${detailOf(err)}`);
      },
    );
  },

  preview: (doc) => previewWordsearch(payloadFrom(doc)).then(() => ({ id: 0, indices: NO_INDICES })),

  duplicate: duplicateJogo,

  Canvas,
  Inspector,
};
